import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Mail, ShieldCheck, KeyRound, CheckCircle2, Loader2 } from 'lucide-react';
import api from '@/services/api';
import { toast } from '@/hooks/useToast';

// ---------- schemas ----------
const emailSchema = z.object({
  email: z.string().email('Enter a valid email address'),
});

const codeSchema = z.object({
  code: z.string().length(6, 'Code must be 6 digits').regex(/^\d+$/, 'Code must be digits only'),
});

const passwordSchema = z.object({
  password: z.string()
    .min(8, 'At least 8 characters')
    .regex(/[A-Z]/, 'Need an uppercase letter')
    .regex(/[a-z]/, 'Need a lowercase letter')
    .regex(/[0-9]/, 'Need a number')
    .regex(/[^A-Za-z0-9]/, 'Need a special character'),
  confirmPassword: z.string(),
}).refine((d) => d.password === d.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});

type Step = 'email' | 'code' | 'password' | 'done';

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('email');
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [resetToken, setResetToken] = useState('');

  // ----- step 1: email -----
  const emailForm = useForm<z.infer<typeof emailSchema>>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: '' },
  });

  const onSubmitEmail = async (data: z.infer<typeof emailSchema>) => {
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: data.email });
      setEmail(data.email);
      setStep('code');
      toast({ title: 'Code sent', description: 'Check your email inbox for the 6-digit code.' });
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Failed to send code';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // ----- step 2: 6-digit code -----
  const [codeDigits, setCodeDigits] = useState<string[]>(['', '', '', '', '', '']);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const codeForm = useForm<z.infer<typeof codeSchema>>({
    resolver: zodResolver(codeSchema),
    defaultValues: { code: '' },
  });

  useEffect(() => {
    if (step === 'code') inputRefs.current[0]?.focus();
  }, [step]);

  const handleCodeChange = (idx: number, val: string) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...codeDigits];
    next[idx] = val;
    setCodeDigits(next);
    codeForm.setValue('code', next.join(''));
    if (val && idx < 5) inputRefs.current[idx + 1]?.focus();
  };

  const handleCodeKeyDown = (idx: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !codeDigits[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
  };

  const handleCodePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const next = [...codeDigits];
    text.split('').forEach((ch, i) => { next[i] = ch; });
    setCodeDigits(next);
    codeForm.setValue('code', next.join(''));
    inputRefs.current[Math.min(text.length, 5)]?.focus();
  };

  const onSubmitCode = async (data: z.infer<typeof codeSchema>) => {
    setLoading(true);
    try {
      const res = await api.post('/auth/verify-reset-code', { email, code: data.code });
      setResetToken(res.data.resetToken);
      setStep('password');
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Invalid or expired code';
      toast({ title: 'Verification failed', description: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // ----- step 3: new password -----
  const pwForm = useForm<z.infer<typeof passwordSchema>>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { password: '', confirmPassword: '' },
  });

  const onSubmitPassword = async (data: z.infer<typeof passwordSchema>) => {
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token: resetToken, password: data.password });
      setStep('done');
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Failed to reset password';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // ----- UI -----
  const steps: { key: Step; label: string; icon: React.ReactNode }[] = [
    { key: 'email', label: 'Email', icon: <Mail className="w-4 h-4" /> },
    { key: 'code', label: 'Verify', icon: <ShieldCheck className="w-4 h-4" /> },
    { key: 'password', label: 'Reset', icon: <KeyRound className="w-4 h-4" /> },
  ];
  const stepIdx = steps.findIndex((s) => s.key === step);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-8 text-center text-white">
            <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              {step === 'done' ? <CheckCircle2 className="w-7 h-7" /> : <KeyRound className="w-7 h-7" />}
            </div>
            <h1 className="text-xl font-bold">
              {step === 'done' ? 'Password Reset!' : 'Reset Your Password'}
            </h1>
            {step !== 'done' && (
              <p className="text-blue-100 text-sm mt-1">
                {step === 'email' && 'Enter your email to receive a reset code'}
                {step === 'code' && 'Enter the 6-digit code sent to your email'}
                {step === 'password' && 'Choose a new secure password'}
              </p>
            )}
          </div>

          {/* Step indicators */}
          {step !== 'done' && (
            <div className="flex items-center justify-center gap-2 py-4 bg-slate-50 border-b">
              {steps.map((s, i) => (
                <div key={s.key} className="flex items-center gap-2">
                  <div
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      i <= stepIdx
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-slate-100 text-slate-400'
                    }`}
                  >
                    {s.icon}
                    {s.label}
                  </div>
                  {i < steps.length - 1 && (
                    <div className={`w-6 h-0.5 ${i < stepIdx ? 'bg-blue-300' : 'bg-slate-200'}`} />
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="p-6">
            {/* Step 1: Email */}
            {step === 'email' && (
              <form onSubmit={emailForm.handleSubmit(onSubmitEmail)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      {...emailForm.register('email')}
                      type="email"
                      placeholder="name@company.com"
                      autoFocus
                      className="w-full h-12 pl-11 pr-4 text-sm border-2 border-slate-200 rounded-xl outline-none transition-all focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                    />
                  </div>
                  {emailForm.formState.errors.email && (
                    <p className="text-red-500 text-xs mt-1">{emailForm.formState.errors.email.message}</p>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-xl transition-all disabled:opacity-50"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send Reset Code'}
                </button>
              </form>
            )}

            {/* Step 2: Code */}
            {step === 'code' && (
              <form onSubmit={codeForm.handleSubmit(onSubmitCode)} className="space-y-4">
                <p className="text-sm text-slate-500 text-center">
                  We sent a code to <strong className="text-slate-700">{email}</strong>
                </p>
                <div className="flex justify-center gap-2" onPaste={handleCodePaste}>
                  {codeDigits.map((d, i) => (
                    <input
                      key={i}
                      ref={(el) => { inputRefs.current[i] = el; }}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={d}
                      onChange={(e) => handleCodeChange(i, e.target.value)}
                      onKeyDown={(e) => handleCodeKeyDown(i, e)}
                      className="w-12 h-14 text-center text-xl font-bold border-2 border-slate-200 rounded-xl outline-none transition-all focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                    />
                  ))}
                </div>
                {codeForm.formState.errors.code && (
                  <p className="text-red-500 text-xs text-center">{codeForm.formState.errors.code.message}</p>
                )}
                <button
                  type="submit"
                  disabled={loading || codeDigits.join('').length < 6}
                  className="w-full h-12 flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-xl transition-all disabled:opacity-50"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verify Code'}
                </button>
                <button
                  type="button"
                  onClick={() => { setStep('email'); setCodeDigits(['', '', '', '', '', '']); }}
                  className="w-full text-sm text-slate-500 hover:text-blue-600 transition-colors"
                >
                  Didn't receive it? Try again
                </button>
              </form>
            )}

            {/* Step 3: New Password */}
            {step === 'password' && (
              <form onSubmit={pwForm.handleSubmit(onSubmitPassword)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">New Password</label>
                  <input
                    {...pwForm.register('password')}
                    type="password"
                    placeholder="Enter new password"
                    autoFocus
                    className="w-full h-12 px-4 text-sm border-2 border-slate-200 rounded-xl outline-none transition-all focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                  />
                  {pwForm.formState.errors.password && (
                    <p className="text-red-500 text-xs mt-1">{pwForm.formState.errors.password.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Confirm Password</label>
                  <input
                    {...pwForm.register('confirmPassword')}
                    type="password"
                    placeholder="Confirm new password"
                    className="w-full h-12 px-4 text-sm border-2 border-slate-200 rounded-xl outline-none transition-all focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                  />
                  {pwForm.formState.errors.confirmPassword && (
                    <p className="text-red-500 text-xs mt-1">{pwForm.formState.errors.confirmPassword.message}</p>
                  )}
                </div>
                <div className="text-xs text-slate-400 space-y-0.5">
                  <p>Password must contain:</p>
                  <ul className="list-disc pl-4">
                    <li>At least 8 characters</li>
                    <li>Uppercase &amp; lowercase letters</li>
                    <li>A number and a special character</li>
                  </ul>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-xl transition-all disabled:opacity-50"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Reset Password'}
                </button>
              </form>
            )}

            {/* Step 4: Done */}
            {step === 'done' && (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-8 h-8 text-green-600" />
                </div>
                <p className="text-slate-600">Your password has been reset successfully.</p>
                <button
                  onClick={() => navigate('/login')}
                  className="w-full h-12 flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-xl transition-all"
                >
                  Go to Login
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Back link */}
        {step !== 'done' && (
          <div className="text-center mt-6">
            <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-blue-600 transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back to Login
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
